// Height of the fixed home page header (h-20)
const HEADER_OFFSET = 80;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Document position from layout offsets, so in-progress reveal transforms don't skew it
const layoutTop = (el: HTMLElement) => {
  let top = 0;
  let node: HTMLElement | null = el;
  while (node) {
    top += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return top;
};

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

let cancelCurrent: (() => void) | null = null;

// `getTargetY` is re-read every frame so late layout shifts (fonts, images) are followed
export const smoothScrollTo = (getTargetY: () => number) => {
  cancelCurrent?.();

  const clamp = (y: number) =>
    Math.max(
      0,
      Math.min(y, document.documentElement.scrollHeight - window.innerHeight),
    );
  const startY = window.scrollY;
  const distance = clamp(getTargetY()) - startY;

  if (prefersReducedMotion() || Math.abs(distance) < 2) {
    window.scrollTo(0, clamp(getTargetY()));
    return;
  }

  // Longer jumps take a little longer, within comfortable bounds
  const duration = Math.min(1200, Math.max(500, Math.abs(distance) * 0.25));
  const start = performance.now();
  let frame = 0;

  // Stop the animation if the user starts scrolling themselves
  const stop = () => {
    cancelAnimationFrame(frame);
    window.removeEventListener("wheel", stop);
    window.removeEventListener("touchstart", stop);
    window.removeEventListener("keydown", stop);
    cancelCurrent = null;
  };
  window.addEventListener("wheel", stop, { passive: true });
  window.addEventListener("touchstart", stop, { passive: true });
  window.addEventListener("keydown", stop);
  cancelCurrent = stop;

  const step = (now: number) => {
    const progress = Math.min(1, (now - start) / duration);
    const endY = clamp(getTargetY());
    window.scrollTo(0, startY + (endY - startY) * easeInOutCubic(progress));
    if (progress < 1) {
      frame = requestAnimationFrame(step);
    } else {
      stop();
    }
  };
  frame = requestAnimationFrame(step);
};

export const smoothScrollToElement = (el: HTMLElement) => {
  smoothScrollTo(() => (el.id === "top" ? 0 : layoutTop(el) - HEADER_OFFSET));
};

// Intercepts clicks on in-page links (href="#section")
export const handleAnchorClick = (event: MouseEvent) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(
    'a[href^="#"]',
  );
  const id = link?.getAttribute("href")?.slice(1);
  const target = id ? document.getElementById(id) : null;
  if (!target) return;

  event.preventDefault();
  window.history.replaceState(window.history.state, "", `#${id}`);
  smoothScrollToElement(target);
};
